<template>
  <div id="code-editor" ref="editor"></div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
</style>

<script>
// @flow
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api' // needs to be imported this way in order to include only subset of languages
import _ from 'lodash'
import { Flow, BackgroundScheduler } from 'services'
import { mapActions, mapGetters } from 'vuex'

const markerOwners = ['flow-refs-errors', 'flow-refs-warnings', 'javascript']

export default {
  name: 'MonacoEditor',
  data() {
    return {
      tsErrorsAsWarnings: [
        '2339', // property doesn't exist
        '2554' // Fewer than expected arguments to function call
      ],
      tsDisabledMarkers: [
        '7043', // ... a better type may be inferred from usage...
        '7044' // ... a better type may be inferred from usage...
      ],
      editor: null,
      model: null,
      adhocModel: null,
      options: {
        minimap: {
          enabled: false,
          side: 'left'
        },
        suggest: {
          showKeywords: false,
          showSnippets: true,
          showIcons: true
        },
        wordBasedSuggestions: false,
        quickSuggestions: false,
        automaticLayout: true,
        tabCompletion: 'on',
        readOnly: this.readonly,
        scrollBeyondLastLine: false
      },
      completionProvider: null,
      decorations: [],
      suggestions: word => this.completions(word).then(this.mapSuggestionsToCompletions),
      debouncedOnCursorChanged: _.debounce(this.onCursorChanged, 1000),
      debouncedOnContentChanged: _.debounce(this.onContentChanged, 1000),
      debouncedExternalLibSetter: _.debounce(() => {
        this.setupExtraLib()
        this.setSyntaxValidation()
      }, 500),
      extraLib: null,
      lodashCleanups: [],
      gotoProviderDisposable: null,
      originalSetModelMarkers: null,
      lastExtraLibSource: '',
      themeInitialized: this.userPrefsInitialized
    }
  },

  props: {
    value: {
      type: Object,
      default: () => {}
    },
    readonly: {
      type: Boolean,
      default: () => false
    },
    completions: {
      type: Function,
      default: word => {}
    },
    showMinimap: {
      type: Boolean,
      default: () => false
    },
    allFlows: {
      type: Array,
      default: () => []
    },
    gotoableFields: {
      type: Array,
      default: () => []
    },
    language: {
      type: String,
      default: () => null
    },
    flowId: {
      type: String,
      default: () => ''
    },
    extraLibSource: {
      type: Object,
      default: () => {}
    },
    squiggles: {
      type: Object,
      default: () => ({ errors: [], warnings: [] })
    },
    checkDisabledLines: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    ...mapGetters('flowEditor', ['isDarkThemeActive']),
    ...mapGetters(['userPrefsInitialized']),
    shouldTranspile() {
      return this.language === Flow.supportedLanguages.MODERN
    },
    cursorContext() {
      const cursorPosition = this.editor.getPosition()
      const cursorWord = cursorPosition
        ? this.editor.getModel().getWordAtPosition(cursorPosition)
        : null
      const cursorLine = cursorPosition
        ? this.editor
            .getModel()
            .getLineContent(cursorPosition.lineNumber)
            .trim()
        : null

      return {
        ...cursorPosition,
        word: cursorLine && cursorLine.endsWith('.') ? null : cursorWord ? cursorWord.word : '',
        line: cursorLine || ''
      }
    },
    cursorWord() {
      return this.cursorContext.word
    }
  },

  methods: {
    ...mapActions('debugger', ['runCodeInEditor']),
    triggerLayout() {
      setTimeout(() => this.editor.layout(), 200)
    },
    setUpEditor() {
      this.clearOldMonacoModels()
      this.setUpEditorTheme()
      this.setUpCompilerOptions()

      // A monaco model represents the file being edited
      const initialCode = this.value.code || this.value.es5Code || ''
      this.model = monaco.editor.createModel(
        initialCode,
        'typescript',
        monaco.Uri.parse('flow://x/FLOWNAME') // This could be replaced with flow name in mapped stack traces
      )
      this.model.updateOptions({ indentSize: 2, tabSize: 2, insertSpaces: true })
      this.adhocModel = monaco.editor.createModel('', 'typescript')
      this.editor = monaco.editor.create(this.$el, {
        model: this.model,
        readOnly: this.readOnly,
        ...this.options
      })

      this.debouncedExternalLibSetter()

      this.setUpEditorEvents()

      this.registerGoToProvider()

      this.registerCustomActions(this.editor)

      this.setupLodashTypes()

      this.setupMarkerIntercept()
    },

    setupLodashTypes() {
      const ctx = require.context('@types/lodash', true, /\.d\.ts$/)
      // Reduce all the .d.ts files to a filename => content object
      const allLibs = ctx
        .keys()
        .filter(k => k.indexOf('fp/') < 0)
        .reduce((acc, k) => {
          acc[k] = ctx(k).default
          return acc
        }, {})

      // Add every lib from the typescript source
      this.lodashCleanups = Object.entries(allLibs).map(([filename, content]) => {
        return monaco.languages.typescript.typescriptDefaults.addExtraLib(content, filename)
      })
    },

    getModelWorker(model) {
      return monaco.languages.typescript
        .getTypeScriptWorker()
        .then(workerGetter => workerGetter(model.uri))
    },

    setupExtraLib() {
      if (
        !this.extraLibSource ||
        !this.extraLibSource.source ||
        this.extraLibSource.source === this.lastExtraLibSource
      ) {
        return
      }
      if (this.extraLib) this.extraLib.dispose()
      this.extraLib = monaco.languages.typescript.typescriptDefaults.addExtraLib(
        this.extraLibSource.source
      )
      this.lastExtraLibSource = this.extraLibSource.source
    },

    clearOldMonacoModels() {
      monaco.editor.getModels().forEach(e => e.dispose())
    },

    registerActionShortcut(editor, eventId, description, keyBinding, callbackFn) {
      editor.addAction({
        id: 'sirenia-' + eventId,
        label: description,
        keybindings: [keyBinding],
        run: callbackFn
      })
    },

    registerCustomActions(editor) {
      this.registerActionShortcut(
        editor,
        'run-flow',
        'Run the flow',
        monaco.KeyMod.chord(monaco.KeyCode.F10),
        () => this.runCodeInEditor(false)
      )
      this.registerActionShortcut(
        editor,
        'debug-flow',
        'Debug the flow',
        monaco.KeyMod.chord(monaco.KeyCode.F11),
        () => this.runCodeInEditor(true)
      )
    },

    setUpEditorTheme() {
      monaco.editor.setTheme(this.isDarkThemeActive ? 'vs-dark' : 'vs')
    },

    setUpCompilerOptions() {
      monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES5,
        lib: ['es5'],
        allowNonTsExtensions: true,
        sourceMap: true
      })
    },

    setSyntaxValidation() {
      const disableValidation = !this.extraLibSource.hasApiDefinitions
      monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: disableValidation
      })
    },

    getTranspilation(model) {
      if (!this.shouldTranspile) return Promise.resolve({})
      return this.getModelWorker(model).then(worker => {
        return worker.getEmitOutput(model.uri.toString()).then(r => {
          const transpilation =
            r.outputFiles.length > 1
              ? {
                  sourcemap: r.outputFiles[0].text,
                  code: r.outputFiles[1].text
                }
              : null
          if (!transpilation) {
            console.error("Didn't get expected files from monaco transpiler", r)
            throw Error('No files from transpiler')
          }
          return transpilation
        })
      })
    },

    getContentObject(model) {
      // The code property is for displayed code transpiling. If we aren't transpiling, then just populate the es5Code property
      const content = model.getValue()
      if (!content || !this.shouldTranspile) return Promise.resolve({ es5Code: content })
      return this.getTranspilation(model).then(transpilation => {
        return {
          code: content,
          es5Code: transpilation.code,
          sourcemap: transpilation.sourcemap
        }
      })
    },

    signalSave() {
      this.saveRequested = true
      return this.changePending
    },

    onContentChanged() {
      const newContent = this.model.getValue()
      if (newContent === (this.value.code || this.value.es5Code)) return // No change - ignore
      this.getContentObject(this.model)
        .then(contentObject => {
          contentObject.saveRequested = this.saveRequested
          this.$emit('input', contentObject)
          this.$emit('changed', contentObject)
          this.changePending = false
        })
        .catch(e => console.warn('Error transpiling code change', e))
    },

    onCursorChanged() {
      // Use the adhocModel for compiling the selected text
      if (!this.shouldTranspile) {
        this.$emit('codeSelected', { es5Code: this.getSelectedCode() || '' })
        return
      }
      // No cursor events until adhocModel is initialized
      this.adhocModel.setValue(this.getSelectedCode() || '')
      this.getContentObject(this.adhocModel)
        .then(contentObject => {
          this.$emit('codeSelected', contentObject)
          this.adhocModel.setValue('')
        })
        .catch(e => console.warn('Error transpiling selection', e))
    },

    setModelMarkerIntercept(model, owner, markers) {
      if (owner === 'typescript') {
        markers = markers.filter(m => {
          if (this.checkDisabledLines.indexOf(m.startLineNumber) >= 0) return false
          if (this.tsDisabledMarkers.indexOf(m.code) >= 0) return false
          if (
            this.tsErrorsAsWarnings.indexOf(m.code) >= 0 &&
            m.severity === monaco.MarkerSeverity.Error
          ) {
            m.severity = monaco.MarkerSeverity.Warning
          }
          return true
        })
      }
      this.originalSetModelMarkers.apply(monaco.editor, [model, owner, markers])
    },

    setupMarkerIntercept() {
      // HACK: monaco internally calls its exposed function 'setModelMarkers'. By overriding it, we gain access to
      // filtering and altering levels of the typescript markers. NOTE: It's important that the original is restored
      // before this component is destroyed. A sanctioned API may materialize for this eventually.
      this.originalSetModelMarkers = monaco.editor.setModelMarkers
      monaco.editor.setModelMarkers = (model, owner, markers) => {
        this.setModelMarkerIntercept(model, owner, markers)
      }
    },

    setUpEditorEvents() {
      this.editor.onDidChangeModelContent(() => {
        this.saveRequested = false // Keep track of save requests between here and the emitting of the change event
        this.changePending = true
        this.debouncedOnContentChanged()
      })
      this.editor.onDidChangeCursorPosition(this.debouncedOnCursorChanged)
      this.defineCompletions()
    },

    beautifyCode() {
      this.editor.getAction('editor.action.formatDocument').run()
    },

    focus() {
      this.editor.focus()
    },

    toggleMinimap() {
      this.editor.updateOptions({
        minimap: {
          enabled: this.showMinimap,
          side: 'left'
        }
      })
    },

    insertSuggestion(suggestion) {
      const sc = this.editor.getContribution('snippetController2')
      if (sc) {
        sc.insert(suggestion)
      } else {
        this.editor.executeEdits('', [
          {
            range: new monaco.Range(
              this.cursorContext.lineNumber,
              this.cursorContext.end,
              this.cursorContext.lineNumber,
              this.cursorContext.end
            ),
            forceMoveMarkers: true,
            /// Convert the snippet placeholder syntax to simple text
            text: suggestion.replace(/\$\{\d+:(\w+)\}/g, (m, name) => name)
          }
        ])
      }
    },

    defineCompletions() {
      if (this.completionProvider) {
        this.completionProvider.dispose()
      }

      this.completionProvider = monaco.languages.registerCompletionItemProvider('typescript', {
        provideCompletionItems: (document, position, token, context) =>
          this.suggestions(this.cursorWord)
      })
    },

    mapSuggestionsToCompletions(suggestions) {
      return {
        suggestions: _.map(suggestions, s => {
          return {
            label: s.display || s.text || '',
            filterText: s.trigger + s.text,
            details: s.display || s.documentation,
            documentation: s.documentation || s.text,
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            insertText: s.text
          }
        })
      }
    },

    registerGoToProvider() {
      this.gotoProviderDisposable = monaco.languages.registerDefinitionProvider('typescript', {
        provideDefinition: (model, position, cancellationToken) => {
          /**
           * Get the word, phrase, whatever the definition for is needed
           * Detect if it's a flow, or field
           * Redirect
           */
          const wordAtCursor = this.model.getWordAtPosition(position)
          if (!wordAtCursor) return null
          const cursorOffset = this.model.getOffsetAt(position)
          const matchContainsCursor = match =>
            match.index <= cursorOffset && match.index + match.length >= cursorOffset
          return BackgroundScheduler.defaultScheduler
            .scheduleCodeAnalysis(this.value.code || this.value.es5Code, !!this.value.code)
            .then(analysis => {
              const fieldRef = _.find(analysis.fieldRefs, fr => {
                return fr.matchedRef.indexOf(wordAtCursor.word) >= 0 && matchContainsCursor(fr)
              })
              if (fieldRef) {
                let field = _.find(this.gotoableFields, f => f.name === fieldRef.matchedRef)
                if (field) {
                  const route = this.$router.resolve({
                    name: 'app-field',
                    params: { appId: field.application, fieldId: field.id }
                  }).href
                  return {
                    uri: monaco.Uri.parse(`${window.location.origin}${route}`),
                    range: new monaco.Range(0, 0, 0, 0)
                  }
                }
              }
              const flowRef = _.find(
                analysis.flowRefs,
                fr => fr.matchedRef.indexOf(wordAtCursor.word) >= 0 && matchContainsCursor(fr)
              )
              if (flowRef) {
                let flow = _.find(
                  this.allFlows,
                  f =>
                    f.name === flowRef.matchedRef ||
                    f.typeConfig.subjectPrefix === flowRef.matchedRef
                )
                if (!_.isEmpty(flow)) {
                  const route = this.$router.resolve({
                    name: 'app-flow',
                    params: {
                      appId: flow.application,
                      flowId: flow.id,
                      mode: 'code'
                    }
                  }).href
                  return {
                    uri: monaco.Uri.parse(`${window.location.origin}${route}`),
                    range: new monaco.Range(0, 0, 0, 0)
                  }
                }
              }
              return null
            })
        }
      })
    },

    getSelectedCode() {
      return this.editor.getModel().getValueInRange(this.editor.getSelection())
    },

    renderIfReady() {
      if (this.userPrefsInitialized) {
        this.setUpEditorTheme()
        this.themeInitialized = true // Avoid momentarily showing with wrong theme
        this.triggerLayout()
      }
    },

    updateSquiggles() {
      const toMarker = (squiggle, severity) => {
        const startPos = this.model.getPositionAt(squiggle.index)
        const endPos = this.model.getPositionAt(squiggle.index + squiggle.length)
        return {
          startLineNumber: startPos.lineNumber,
          startColumn: startPos.column,
          endLineNumber: endPos.lineNumber,
          endColumn: endPos.column,
          message: squiggle.message,
          severity
        }
      }
      const errorMarkers = (this.squiggles.errors || []).map(squiggle =>
        toMarker(squiggle, monaco.MarkerSeverity.Error)
      )
      monaco.editor.setModelMarkers(this.model, 'flow-refs-errors', errorMarkers)
      const warningMarkers = (this.squiggles.warnings || []).map(squiggle =>
        toMarker(squiggle, monaco.MarkerSeverity.Warning)
      )
      monaco.editor.setModelMarkers(this.model, 'flow-refs-warnings', warningMarkers)
      if (window.logMarkers) {
        console.debug(
          'Total number of markers (errors/warnings/total incl automatic ones) 500 total is max!',
          ...markerOwners.map(o => monaco.editor.getModelMarkers({ owner: o }).length)
        )
      }
    },

    // NOTE: never call this method while the user is editing. He won't like it.
    updateContents(newContents) {
      const curContent = this.model.getValue()
      if (newContents.code === curContent || newContents.es5Code === curContent) return
      // Clear all markers
      markerOwners.forEach(o => monaco.editor.setModelMarkers(this.model, o, []))
      this.editor.setValue(newContents.code || newContents.es5Code)
      // Reset cursor position and scroll state
      this.editor.setPosition({ column: 0, lineNumber: 0 })
      this.editor.setScrollTop(0)
      this.editor.setScrollLeft(0)
    }
  },

  mounted() {
    this.setUpEditor()
    this.renderIfReady()
  },

  beforeDestroy() {
    if (this.extraLib) this.extraLib.dispose()
    if (this.completionProvider) this.completionProvider.dispose()
    if (this.lodashCleanups) this.lodashCleanups.forEach(x => x.dispose())
    if (this.gotoProviderDisposable) this.gotoProviderDisposable.dispose()
    if (this.originalSetModelMarkers) monaco.editor.setModelMarkers = this.originalSetModelMarkers
  },

  watch: {
    // Needs to be explicitly added to the editor
    extraLibSource() {
      this.debouncedExternalLibSetter()
    },
    flowId(newVal) {
      if (!newVal || !this.value) return
      this.updateContents(this.value)
    },
    readonly(newVal) {
      this.editor.updateOptions({ readOnly: newVal })
    },
    showMinimap() {
      this.toggleMinimap()
    },
    userPrefsInitialized() {
      this.renderIfReady()
    },
    squiggles() {
      this.updateSquiggles()
    },
    checkDisabledLines(newVal, oldVal) {
      // Run current markers through our marker intercept filter again in order to have squiggles go away immediately
      if (newVal && oldVal && newVal.length !== oldVal.length) {
        monaco.editor.setModelMarkers(
          this.model,
          'typescript',
          monaco.editor.getModelMarkers('typescript')
        )
      }
    }
  }
}
</script>
